import React, { useState } from "react";
import { ShieldCheck, FileText, RefreshCw, Scale, Globe, Building, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

export type LegalDocType = "privacy" | "terms" | "refund" | "mor";

interface LegalMoRFooterProps {
  currentSubdomain?: string;
}

export default function LegalMoRFooter({ currentSubdomain = "bhutadamarasena.com" }: LegalMoRFooterProps) {
  const [activeModalDoc, setActiveModalDoc] = useState<LegalDocType | null>(null);

  return (
    <footer className="w-full border-t border-stone-800 bg-stone-950 px-6 py-6 text-xs text-stone-400 mt-12 space-y-4">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Merchant of Record Entity Info */}
        <div className="space-y-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-2">
            <Building className="w-4 h-4 text-amber-400" />
            <span className="font-bold text-stone-200">Aghora Abraham Global LLC</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-semibold">
              Self-Merchant of Record (Self-MoR)
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-sky-500/10 border border-sky-500/30 text-sky-400 font-semibold">
              Stripe Verified
            </span>
          </div>
          <p className="text-[11px] text-stone-500">
            Aghora Abraham Global LLC operates as the direct Merchant of Record. Payments, subscriptions & billing are securely handled via Stripe infrastructure.
          </p>
        </div>

        {/* Legal Links */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-stone-300 font-medium">
          <button
            onClick={() => setActiveModalDoc("privacy")}
            className="hover:text-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
            <span>Privacy Policy</span>
          </button>

          <span className="text-stone-700">•</span>

          <button
            onClick={() => setActiveModalDoc("terms")}
            className="hover:text-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <FileText className="w-3.5 h-3.5 text-amber-400" />
            <span>Terms of Service</span>
          </button>

          <span className="text-stone-700">•</span>

          <button
            onClick={() => setActiveModalDoc("refund")}
            className="hover:text-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5 text-amber-400" />
            <span>Refund & Cancellation</span>
          </button>

          <span className="text-stone-700">•</span>

          <button
            onClick={() => setActiveModalDoc("mor")}
            className="hover:text-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <Scale className="w-3.5 h-3.5 text-amber-400" />
            <span>Self-MoR & Stripe Compliance</span>
          </button>
        </div>
      </div>

      {/* Unified Main Domain Footer Info with Zenodo Citable Record Links */}
      <div className="max-w-7xl mx-auto pt-3 border-t border-stone-900 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] text-stone-500">
        <div className="flex flex-wrap items-center gap-2">
          <Globe className="w-3.5 h-3.5 text-amber-400/70" />
          <span>Platform: <strong className="text-stone-300 font-mono">bhutadamarasena.com/ancient-runes</strong></span>
          <span className="text-stone-700">•</span>
          <span>Preprint: <a href="https://doi.org/10.5281/zenodo.23361838" target="_blank" rel="noreferrer" className="text-amber-400/90 hover:underline font-mono">10.5281/zenodo.23361838</a></span>
          <span className="text-stone-700">•</span>
          <span>Software: <a href="https://doi.org/10.5281/zenodo.23391403" target="_blank" rel="noreferrer" className="text-cyan-400/90 hover:underline font-mono">10.5281/zenodo.23391403</a></span>
        </div>

        <div className="flex items-center gap-3">
          <span>Engineering: <strong className="text-stone-300">BhutaDamaraSena R&D Labs</strong></span>
          <span>Support: <a href="mailto:support@bhutadamarasena.com" className="text-amber-400 hover:underline">support@bhutadamarasena.com</a></span>
        </div>
      </div>

      {/* Modal Dialog for Legal Documents */}
      <AnimatePresence>
        {activeModalDoc && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-950/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-stone-900 border border-stone-700 rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-2xl max-h-[85vh] flex flex-col"
            >
              <div className="flex items-center justify-between border-b border-stone-800 pb-3">
                <div className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-amber-400" />
                  <h3 className="text-base font-bold text-stone-100">
                    {activeModalDoc === "privacy" && "Privacy Policy — BhutaDamaraSena Standard"}
                    {activeModalDoc === "terms" && "Terms of Service & License Agreement"}
                    {activeModalDoc === "refund" && "Refund & Cancellation Policy"}
                    {activeModalDoc === "mor" && "Self-Merchant of Record (Self-MoR) & Stripe Disclosure"}
                  </h3>
                </div>

                <button
                  onClick={() => setActiveModalDoc(null)}
                  className="text-stone-400 hover:text-stone-200 font-bold text-sm px-2 py-1 rounded bg-stone-800 cursor-pointer"
                >
                  ✕
                </button>
              </div>

              {/* Document Text Body */}
              <div className="overflow-y-auto space-y-4 text-xs text-stone-300 pr-2 leading-relaxed font-sans">
                {activeModalDoc === "privacy" && (
                  <div className="space-y-3">
                    <p className="font-bold text-stone-100">Effective Date: January 1, 2026</p>
                    <p>
                      Aghora Abraham Global LLC (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;) and BhutaDamaraSena R&D Labs respect your privacy and are committed to protecting personal data across our primary website (bhutadamarasena.com).
                    </p>
                    <h4 className="font-bold text-amber-300">1. Data Handled & Offline DSP</h4>
                    <p>
                      We collect minimal information necessary to authenticate accounts and synchronize your practice history in Cloudflare D1 SQL. Raw microphone audio and phonetics pitch analysis run entirely client-side via Web Audio API. No audio recordings are retained on external servers without explicit user request.
                    </p>
                    <h4 className="font-bold text-amber-300">2. Payment Security</h4>
                    <p>
                      Payment processing is performed via Stripe. Sensitive credit card credentials and banking tokens are transmitted directly to Stripe via encrypted TLS channels and never touch or reside on our servers.
                    </p>
                    <h4 className="font-bold text-amber-300">3. Contact</h4>
                    <p>
                      For privacy inquiries or data erasure, reach out to support@bhutadamarasena.com.
                    </p>
                  </div>
                )}

                {activeModalDoc === "terms" && (
                  <div className="space-y-3">
                    <p className="font-bold text-stone-100">Terms of Service — Version 3.0</p>
                    <p>
                      By accessing or using the Ancient Runes application on bhutadamarasena.com, you agree to these Terms.
                    </p>
                    <h4 className="font-bold text-amber-300">1. Subscriptions & Self-MoR Billing</h4>
                    <p>
                      All purchases and subscriptions (Pro Tier, Lifetime Founder Pass) are billed by Aghora Abraham Global LLC acting as Self-Merchant of Record (Self-MoR) through Stripe. Subscriptions renew automatically until canceled in your account dashboard.
                    </p>
                    <h4 className="font-bold text-amber-300">2. Intellectual Property</h4>
                    <p>
                      All Ancient Runes acoustic matrix algorithms, epigraphy decoders, DSP phonetics engines, and software modules remain the proprietary property of Aghora Abraham Global LLC (BhutaDamaraSena R&D Labs).
                    </p>
                  </div>
                )}

                {activeModalDoc === "refund" && (
                  <div className="space-y-3">
                    <p className="font-bold text-stone-100">14-Day Money-Back Guarantee</p>
                    <p>
                      We offer a <strong>14-day refund policy</strong> for all subscription tiers and digital passes processed via Stripe.
                    </p>
                    <h4 className="font-bold text-amber-300">How to Request a Refund</h4>
                    <p>
                      1. Email <a href="mailto:support@bhutadamarasena.com" className="text-amber-400 underline">support@bhutadamarasena.com</a> with your email and Stripe payment receipt.<br />
                      2. Our billing team will process your refund via Stripe back to your original payment method within 3–5 business days.
                    </p>
                  </div>
                )}

                {activeModalDoc === "mor" && (
                  <div className="space-y-3">
                    <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-1">
                      <p className="font-bold text-amber-200">Self-Merchant of Record (Self-MoR) & Stripe Infrastructure</p>
                      <p className="text-stone-300">
                        Aghora Abraham Global LLC acts as direct Merchant of Record (Self-MoR). We take direct responsibility for all commercial transactions, customer service, and regulatory compliance, with payment facilitation provided by Stripe.
                      </p>
                    </div>

                    <h4 className="font-bold text-amber-300">Compliance & Entity Details:</h4>
                    <ul className="list-disc list-inside space-y-1 text-stone-300">
                      <li><strong>Merchant of Record & Operating Entity:</strong> Aghora Abraham Global LLC</li>
                      <li><strong>Research & Engineering Division:</strong> BhutaDamaraSena R&D Labs</li>
                      <li><strong>Payment Processing:</strong> Stripe (PCI-DSS Level 1 Certified)</li>
                      <li><strong>Primary Domain:</strong> bhutadamarasena.com</li>
                      <li><strong>Security & Invoicing:</strong> End-to-end encrypted TLS, automated tax compliance, 14-day refund guarantee</li>
                    </ul>
                  </div>
                )}
              </div>

              <div className="border-t border-stone-800 pt-3 flex justify-end">
                <button
                  onClick={() => setActiveModalDoc(null)}
                  className="px-4 py-2 bg-amber-500 text-stone-950 font-bold rounded-xl text-xs hover:bg-amber-400 transition cursor-pointer"
                >
                  Close & Accept
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </footer>
  );
}
